/**
 * CollectOverlay — Physics-based collect mini-game overlay
 *
 * Items are "thrown" onto screen with physics simulation (gravity, bounce,
 * friction). Player clicks each to collect. Auto-collects after timeout.
 * Shows summary when all items collected.
 *
 * Modular: configured via CollectScenarioConfig, not tied to any game system.
 * The trap pull scenario is just one possible configuration.
 *
 * Performance: item positions updated via imperative DOM refs, not React state.
 * Only re-renders when items are collected (removed from DOM tree).
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { usePhysicsSimulation } from '@/hooks/usePhysicsSimulation';
import { CollectItem } from './CollectItem';
import { CollectSummary } from './CollectSummary';
import type { CollectItemData } from './CollectItem';
import type { PhysicsBounds, PhysicsConfig, PhysicsBody } from '@/utils/physics';

// ─── Scenario Configuration ─────────────────────────────────────────

export interface ThrowConfig {
  minVX: number;
  maxVX: number;
  minVY: number;
  maxVY: number;
  minAngular: number;
  maxAngular: number;
  staggerDelayMs: number;
}

export interface CollectScenarioConfig {
  items: CollectItemData[];
  title: string;
  spawnOrigin: { x: number; y: number };
  throwConfig: ThrowConfig;
  physicsConfig?: Partial<PhysicsConfig>;
  autoCollectMs?: number;
  onItemCollected?: (item: CollectItemData) => void;
  onComplete: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function buildPhysicsBody(
  item: CollectItemData,
  spawnX: number,
  spawnY: number,
  throwConfig: ThrowConfig,
): PhysicsBody {
  const size = item.size ?? 64;
  return {
    id: item.id,
    position: { x: spawnX, y: spawnY },
    velocity: {
      x: randomInRange(throwConfig.minVX, throwConfig.maxVX),
      y: randomInRange(throwConfig.minVY, throwConfig.maxVY),
    },
    rotation: randomInRange(-15, 15),
    angularVelocity: randomInRange(throwConfig.minAngular, throwConfig.maxAngular),
    width: size,
    height: size,
    restitution: item.restitution ?? 0.35,
    friction: 1.0,
    settled: false,
  };
}

// ─── Value Popup ─────────────────────────────────────────────────────

interface ValuePopup {
  id: string;
  x: number;
  y: number;
  value: number;
}

// ─── Component ───────────────────────────────────────────────────────

const DEFAULT_AUTO_COLLECT_MS = 8000;

type Phase = 'active' | 'summary';

interface CollectOverlayProps {
  scenario: CollectScenarioConfig | null;
}

export function CollectOverlay({ scenario }: CollectOverlayProps) {
  const [phase, setPhase] = useState<Phase>('active');
  const [collectedValue, setCollectedValue] = useState(0);
  const [collectedCount, setCollectedCount] = useState(0);
  const [remainingItems, setRemainingItems] = useState<CollectItemData[]>([]);
  const [valuePopups, setValuePopups] = useState<ValuePopup[]>([]);
  const [bounds, setBounds] = useState<PhysicsBounds | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const collectingRef = useRef<Set<string>>(new Set());
  const spawnedRef = useRef<Set<string>>(new Set());
  const totalItems = scenario?.items.length ?? 0;

  // Item lookup
  const itemMap = useMemo(() => {
    const map = new Map<string, CollectItemData>();
    scenario?.items.forEach((item) => map.set(item.id, item));
    return map;
  }, [scenario]);

  // Measure container bounds via ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateBounds = () => {
      const rect = container.getBoundingClientRect();
      setBounds({
        left: 0,
        top: 0,
        right: rect.width,
        bottom: rect.height,
      });
    };

    updateBounds();
    const observer = new ResizeObserver(updateBounds);
    observer.observe(container);
    return () => observer.disconnect();
  }, [scenario]);

  // Physics simulation
  const { addBody, removeBody, forceUpdate } = usePhysicsSimulation({
    bounds,
    config: scenario?.physicsConfig,
    onFrame: (snapshot) => {
      for (const body of snapshot.bodies) {
        const el = itemRefs.current.get(body.id);
        if (el) {
          el.style.transform = `translate(${body.position.x - (itemMap.get(body.id)?.size ?? 64) / 2}px, ${body.position.y - (itemMap.get(body.id)?.size ?? 64) / 2}px) rotate(${body.rotation}deg)`;
        }
      }
    },
    enabled: phase === 'active' && bounds !== null,
  });

  // Initialize remaining items and stagger spawn
  useEffect(() => {
    if (!scenario || !bounds) return;

    setPhase('active');
    setCollectedValue(0);
    setCollectedCount(0);
    setValuePopups([]);
    collectingRef.current.clear();
    spawnedRef.current.clear();

    setRemainingItems([...scenario.items]);

    const spawnX = scenario.spawnOrigin.x * bounds.right;
    const spawnY = scenario.spawnOrigin.y * bounds.bottom;

    const timers: ReturnType<typeof setTimeout>[] = [];

    scenario.items.forEach((item, index) => {
      const delay = index * scenario.throwConfig.staggerDelayMs;
      const timer = setTimeout(() => {
        const body = buildPhysicsBody(item, spawnX, spawnY, scenario.throwConfig);
        addBody(body);
        spawnedRef.current.add(item.id);
        // Make item visible
        const el = itemRefs.current.get(item.id);
        if (el) {
          el.style.opacity = '1';
        }
      }, delay);
      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
  }, [scenario, bounds, addBody]);

  // Auto-collect remaining items after timeout
  useEffect(() => {
    if (!scenario || phase !== 'active') return;

    const timeout = scenario.autoCollectMs ?? DEFAULT_AUTO_COLLECT_MS;
    const timer = setTimeout(() => {
      autoCollectAll();
    }, timeout);

    return () => clearTimeout(timer);
  }, [scenario, phase]);

  // Remove old value popups after animation
  useEffect(() => {
    if (valuePopups.length === 0) return;
    const timer = setTimeout(() => {
      setValuePopups([]);
    }, 800);
    return () => clearTimeout(timer);
  }, [valuePopups]);

  // Transition to summary when all collected
  useEffect(() => {
    if (collectedCount > 0 && collectedCount >= totalItems && phase === 'active') {
      const timer = setTimeout(() => setPhase('summary'), 400);
      return () => clearTimeout(timer);
    }
  }, [collectedCount, totalItems, phase]);

  const collectItem = useCallback(
    (item: CollectItemData, x?: number, y?: number) => {
      if (collectingRef.current.has(item.id)) return;
      collectingRef.current.add(item.id);

      // Start collect animation
      const el = itemRefs.current.get(item.id);
      if (el) {
        el.classList.add('collect-pop');
        el.style.pointerEvents = 'none';
      }

      // Show value popup
      if (x !== undefined && y !== undefined) {
        setValuePopups((prev) => [
          ...prev,
          { id: `popup-${item.id}`, x, y, value: item.value },
        ]);
      }

      // After animation, remove from physics + DOM
      setTimeout(() => {
        removeBody(item.id);
        setRemainingItems((prev) => prev.filter((i) => i.id !== item.id));
        setCollectedValue((prev) => prev + item.value);
        setCollectedCount((prev) => prev + 1);
        scenario?.onItemCollected?.(item);
        forceUpdate();
      }, 300);
    },
    [removeBody, forceUpdate, scenario],
  );

  const handleItemClick = useCallback(
    (item: CollectItemData, e: React.MouseEvent) => {
      e.stopPropagation();
      const rect = containerRef.current?.getBoundingClientRect();
      const x = rect ? e.clientX - rect.left : undefined;
      const y = rect ? e.clientY - rect.top : undefined;
      collectItem(item, x, y);
    },
    [collectItem],
  );

  const autoCollectAll = useCallback(() => {
    const uncollected = scenario?.items.filter(
      (item) => !collectingRef.current.has(item.id),
    );
    if (!uncollected?.length) return;

    uncollected.forEach((item, index) => {
      setTimeout(() => {
        collectItem(item);
      }, index * 100);
    });
  }, [scenario, collectItem]);

  const handleDismiss = useCallback(() => {
    scenario?.onComplete();
  }, [scenario]);

  if (!scenario) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      {/* Physics container — fills overlay */}
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-hidden"
        onClick={phase === 'summary' ? handleDismiss : undefined}
      >
        {/* Header with running counter */}
        {phase === 'active' && (
          <div className="absolute top-6 left-0 right-0 text-center z-10 pointer-events-none">
            <h2 className="text-2xl font-bold text-yellow-400 drop-shadow-lg">
              {scenario.title}
            </h2>
            <p className="text-sm text-gray-200 mt-1 drop-shadow">
              <span className="font-bold text-yellow-300">{collectedValue}g</span>
              {' '}collected ({collectedCount}/{totalItems})
            </p>
            {collectedCount === 0 && (
              <p className="text-xs text-gray-400 mt-2 animate-pulse">
                Click creatures to collect them!
              </p>
            )}
          </div>
        )}

        {/* Physics items */}
        {phase === 'active' &&
          remainingItems.map((item) => (
            <div
              key={item.id}
              ref={(el) => {
                if (el) itemRefs.current.set(item.id, el);
              }}
              className="absolute cursor-pointer select-none"
              style={{
                width: item.size ?? 64,
                height: item.size ?? 64,
                willChange: 'transform',
                opacity: spawnedRef.current.has(item.id) ? 1 : 0,
                transition: 'opacity 0.1s',
              }}
              onClick={(e) => handleItemClick(item, e)}
            >
              <CollectItem item={item} />
            </div>
          ))}

        {/* Value popups */}
        {valuePopups.map((popup) => (
          <div
            key={popup.id}
            className="absolute pointer-events-none text-yellow-300 font-bold text-sm drop-shadow-lg"
            style={{
              left: popup.x,
              top: popup.y,
              animation: 'valueFloat 0.7s ease-out forwards',
            }}
          >
            +{popup.value}g
          </div>
        ))}

        {/* Summary phase */}
        {phase === 'summary' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <CollectSummary
              title={scenario.title}
              totalItems={totalItems}
              totalValue={collectedValue}
              onDismiss={handleDismiss}
            />
          </div>
        )}
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes collectPop {
          0%   { transform: scale(1); opacity: 1; }
          40%  { transform: scale(1.3); opacity: 1; }
          100% { transform: scale(0.3); opacity: 0; }
        }
        .collect-pop {
          animation: collectPop 0.3s ease-out forwards !important;
        }
        @keyframes valueFloat {
          0%   { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-40px); opacity: 0; }
        }
        @keyframes collectSummarySlideUp {
          from { transform: translateY(30px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
